/* ── listbox.helpers ────────────────────────────────────────────
 * Shared logic for the two single-choice dropdowns, Select and
 * SearchableSelect. Both render their option list into a portal at
 * <body> (so it escapes ancestor `overflow: hidden`), which means two
 * things the browser would normally do for a native <select> are now
 * our job:
 *
 *   1. Keyboard: the options are plain <li>s, not focusable controls,
 *      so arrow keys do nothing unless we track an "active" option
 *      ourselves and point the focused element at it with
 *      aria-activedescendant (the ARIA combobox pattern). Type-to-
 *      select (typing "exp" to jump to Express) is the same story —
 *      free on a native <select>, ours to write here.
 *   2. Placement: a `position: fixed` popup does not know where the
 *      viewport ends. Without help it always opens below the trigger,
 *      even when the trigger is at the bottom of the screen and the
 *      last options land off-screen where they cannot be clicked.
 *
 * Everything in the first half of this file is pure (numbers and
 * arrays in, numbers out) so it can be unit-tested without a browser.
 * The two DOM helpers at the bottom only READ layout and never touch
 * the window's scroll position.
 * ────────────────────────────────────────────────────────────── */

/* ── Keyboard navigation ─────────────────────────────────────── */

// Select supports `<option disabled>` — callers use disabled rows as
// group headings — so every move skips them. SearchableSelect options
// have no disabled flag, so for them every row is enabled.
export const isOptionEnabled = (option) => !!option && !option.disabled;

export const firstEnabledIndex = (options) => options.findIndex(isOptionEnabled);

export const lastEnabledIndex = (options) => {
  for (let i = options.length - 1; i >= 0; i -= 1) {
    if (isOptionEnabled(options[i])) return i;
  }
  return -1;
};

// Walk from `from` in direction `step` (+1 / -1) to the next enabled
// option. Deliberately does NOT wrap around: a native <select> stops at
// the ends too, and wrapping makes it easy to overshoot a short list.
// Returns `from` unchanged when there is nothing further that way.
export const stepEnabledIndex = (options, from, step) => {
  for (let i = from + step; i >= 0 && i < options.length; i += step) {
    if (isOptionEnabled(options[i])) return i;
  }
  return from;
};

// Where the highlight starts when the list opens: on the current value
// (so Enter without moving re-confirms it, like a native select), or on
// the first enabled row when nothing is selected.
export const initialActiveIndex = (options, isSelected) => {
  const selected = options.findIndex((o) => isOptionEnabled(o) && isSelected(o));
  return selected >= 0 ? selected : firstEnabledIndex(options);
};

// Maps a navigation key to the next active index. Returns `null` for
// keys that are not navigation keys so the caller can fall through to
// its own handling (Enter, Escape, Tab, typing…).
//
// `current` may be -1 (nothing highlighted yet, e.g. the search just
// emptied the list and refilled it). ArrowDown then starts at the top
// and ArrowUp at the bottom, matching the WAI-ARIA combobox pattern.
export const nextActiveIndex = (key, options, current) => {
  const hasCurrent = current >= 0 && current < options.length;
  switch (key) {
    case 'ArrowDown':
      return hasCurrent ? stepEnabledIndex(options, current, 1) : firstEnabledIndex(options);
    case 'ArrowUp':
      return hasCurrent ? stepEnabledIndex(options, current, -1) : lastEnabledIndex(options);
    case 'Home':
      return firstEnabledIndex(options);
    case 'End':
      return lastEnabledIndex(options);
    default:
      return null;
  }
};

/* ── Type-to-select ──────────────────────────────────────────── */

// How long a run of typing stays "one word". Press another key within
// this many milliseconds and the characters join up, so "e" then "x"
// searches for "ex" (Express) rather than for "x"; pause longer and the
// next key starts a fresh search. ~700ms is what Windows and macOS give
// a native <select>, and what the WAI-ARIA listbox pattern suggests.
// This matters because Select REPLACES a native <select>: the browser
// used to do type-ahead for free, and a custom div does not.
export const TYPEAHEAD_RESET_MS = 700;

// Which key presses count as typing.
//   `key.length === 1` rules out every named key — Enter, Escape, Tab,
//   ArrowDown, F5 — because their `key` is a whole word.
//   the \S test rules out the space bar, which the dropdown has already
//   promised to "open the list / pick the highlighted row". Losing the
//   space inside "Front Counter" is a far smaller loss than breaking
//   the key a keyboard user presses to choose something.
export const isTypeAheadKey = (key) =>
  typeof key === 'string' && key.length === 1 && /\S/.test(key);

// Fold one more keystroke into the buffer. `now` is passed in (rather
// than read from Date.now() here) so this stays pure and a test can
// jump forward in time without fake timers.
export const appendTypeAheadKey = (buffer, key, now) => {
  const stale = !buffer || !buffer.str || now - buffer.at > TYPEAHEAD_RESET_MS;
  return { str: (stale ? '' : buffer.str) + key, at: now };
};

/**
 * Which row a typed query should highlight — or -1 for "no match, leave
 * the highlight alone".
 *
 *   options  the rows, in display order
 *   query    everything typed in this run ("ex")
 *   from     where to start looking, normally the row AFTER the
 *            highlighted one. Starting one past the current row is what
 *            makes pressing "c" repeatedly walk through Counter 1,
 *            Counter 2, Counter 3 instead of sticking on the first.
 *            The search wraps around the end of the list, so a
 *            refinement ("c" → "co") still finds the row it is already
 *            standing on.
 *
 * Disabled rows (callers use them as group headings) are skipped, the
 * same way the arrow keys skip them.
 */
export const typeAheadIndex = (options, query, from = 0) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle || !options || options.length === 0) return -1;

  // Normalise `from` into the list and build the wrapped visit order.
  const start = ((Math.trunc(from) % options.length) + options.length) % options.length;
  const visit = [];
  for (let n = 0; n < options.length; n += 1) visit.push((start + n) % options.length);

  const startsWith = (index, text) => {
    const option = options[index];
    if (!isOptionEnabled(option)) return false;
    return String(option.label || '').trim().toLowerCase().startsWith(text);
  };

  // Whole query first: that is what the person typed.
  const exact = visit.find((i) => startsWith(i, needle));
  if (exact !== undefined) return exact;

  // "ppp" is not a word anyone is typing — it is someone pressing "p"
  // to step through every row starting with P, which is how a native
  // <select> behaves. Fall back to the single letter in that one case,
  // never for a genuine mistyped word (that keeps the highlight still,
  // which is the honest answer).
  const sameLetterRun = needle.split('').every((c) => c === needle[0]);
  if (sameLetterRun) {
    const letter = visit.find((i) => startsWith(i, needle[0]));
    if (letter !== undefined) return letter;
  }

  return -1;
};

/* ── Popup placement ─────────────────────────────────────────── */

// Gap between the trigger edge and the popup. 4px is what both
// components used before this helper existed, so nothing shifts.
export const POPUP_GAP = 4;
// Breathing room kept between the popup and the viewport edge, so the
// popup's shadow is not sliced off by the screen edge.
export const VIEWPORT_MARGIN = 8;
// Never shrink the popup below this, even in a pathological viewport —
// a popup a few px tall is worse than one that pokes past the edge.
// ~2 option rows, or the search bar plus one row.
export const MIN_POPUP_HEIGHT = 96;
// The option list's own height cap. MUST match `.list { max-height }`
// in Select.module.scss and SearchableSelect.module.scss — it is how
// tall a list gets when the viewport has room for it.
export const LIST_MAX_HEIGHT = 240;

/**
 * Decide where a fixed-position popup goes relative to its trigger.
 *
 *   trigger        { top, bottom, left, width } — getBoundingClientRect()
 *   viewportHeight the height `position: fixed` measures against
 *   popupHeight    how tall the popup WANTS to be (its content, capped
 *                  by its own CSS max-height) — NOT its clamped height,
 *                  or a popup that was squeezed once would never grow
 *                  back.
 *
 * Rule: open below when the whole popup fits below. Otherwise open on
 * whichever side has more room — flipping upward only when above is
 * actually bigger — and cap the height at the room on that side, so
 * every option is reachable by scrolling the list instead of sitting
 * off-screen.
 *
 * A flipped popup is anchored by its BOTTOM edge (CSS `bottom`), so it
 * stays flush against the trigger whatever height it renders at —
 * including when a search filter shrinks it while it is open.
 */
export const computePopupPlacement = ({
  trigger,
  viewportHeight,
  popupHeight,
  gap = POPUP_GAP,
  margin = VIEWPORT_MARGIN,
  minHeight = MIN_POPUP_HEIGHT,
}) => {
  const spaceBelow = Math.max(0, viewportHeight - trigger.bottom - gap - margin);
  const spaceAbove = Math.max(0, trigger.top - gap - margin);
  const fitsBelow = popupHeight <= spaceBelow;
  const placeAbove = !fitsBelow && spaceAbove > spaceBelow;
  const room = placeAbove ? spaceAbove : spaceBelow;

  return {
    placement: placeAbove ? 'top' : 'bottom',
    top: placeAbove ? null : trigger.bottom + gap,
    bottom: placeAbove ? viewportHeight - trigger.top + gap : null,
    left: trigger.left,
    width: trigger.width,
    maxHeight: Math.max(minHeight, Math.floor(room)),
  };
};

// Cheap equality so scroll/resize handlers (which fire many times a
// second) only cause a re-render when the popup actually has to move.
export const samePlacement = (a, b) =>
  !!a &&
  !!b &&
  a.placement === b.placement &&
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.left === b.left &&
  a.width === b.width &&
  a.maxHeight === b.maxHeight;

/* ── DOM readers ─────────────────────────────────────────────── */

// The box `position: fixed` + `bottom:` is measured against is the
// layout viewport, whose height is documentElement.clientHeight.
// window.innerHeight also counts a horizontal scrollbar, which would
// leave a flipped popup overlapping the trigger by the scrollbar's
// height. jsdom reports clientHeight 0, hence the fallback.
export const getViewportHeight = () =>
  (typeof document !== 'undefined' && document.documentElement.clientHeight) ||
  (typeof window !== 'undefined' ? window.innerHeight : 0);

/**
 * How tall the popup would be if nothing clamped it: its chrome (search
 * bar, borders) plus the option list at its natural height, capped by
 * the list's own CSS max-height. Reading the list's scrollHeight rather
 * than the popup's current height keeps this independent of any
 * maxHeight we already applied — otherwise the measurement would feed
 * back into itself.
 *
 * Returns `fallback` before the popup has mounted (first layout pass).
 */
export const measureNaturalPopupHeight = (popup, list, fallback) => {
  if (!popup) return fallback;
  if (!list) return popup.scrollHeight || fallback;
  const chrome = popup.offsetHeight - list.offsetHeight;
  const cap = parseFloat(window.getComputedStyle(list).maxHeight);
  const listHeight = Number.isFinite(cap)
    ? Math.min(list.scrollHeight, cap)
    : list.scrollHeight;
  return chrome + listHeight || fallback;
};

/**
 * Keep the active option visible inside the scrolling list. Adjusts the
 * LIST's scrollTop only — element.scrollIntoView() would also scroll
 * every scrollable ancestor, including the page behind the popup.
 */
export const scrollOptionIntoView = (list, option) => {
  if (!list || !option) return;
  const listRect = list.getBoundingClientRect();
  const optionRect = option.getBoundingClientRect();
  if (optionRect.top < listRect.top) {
    list.scrollTop -= listRect.top - optionRect.top;
  } else if (optionRect.bottom > listRect.bottom) {
    list.scrollTop += optionRect.bottom - listRect.bottom;
  }
};
